import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, LogOut, Download } from 'lucide-react'
import { useAuthStore } from '@personic/shared'
import { useImpersonationStore } from '@/stores/impersonationStore'
import { navigation, type NavSection } from '@/config/navigation'
import TenantPickerModal from '@/components/admin/TenantPickerModal'

const STORAGE_KEY = 'mindmetal-nav-collapsed'

function getInitialCollapsed(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

export default function AppSidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { isImpersonating, tenantName } = useImpersonationStore()
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(getInitialCollapsed)
  const [tenantPickerTarget, setTenantPickerTarget] = useState<{ name: string; href: string } | null>(null)

  const isStaff = user?.role === 'employee' || user?.role === 'admin' || user?.role === 'superuser'
  const hasNativeTenant = isStaff && !!user?.tenant_id
  const hasTenantContext = isImpersonating || hasNativeTenant || user?.role === 'tenant_admin'

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed))
  }, [collapsed])

  const toggleSection = (id: string) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const isActive = (href: string, end?: boolean) => {
    if (end) return location.pathname === href
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const isSectionActive = (section: NavSection) => {
    if (section.href) return isActive(section.href, true)
    return section.items?.some(item => isActive(item.href)) ?? false
  }

  const canViewSection = (section: NavSection) => {
    if (!user) return false
    return section.roles.includes(user.role as any)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const roleLabel = user?.role === 'superuser' ? 'Superuser'
    : user?.role === 'admin' ? 'Admin'
    : user?.role === 'employee' ? 'Employee'
    : 'Tenant Admin'

  const renderNavItem = (item: { label: string; href: string; icon?: any }, isDeferred: boolean) => {
    const Icon = item.icon
    const active = isActive(item.href)

    if (isDeferred) {
      return (
        <span
          key={item.href}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-warm-300 cursor-not-allowed"
        >
          {Icon && <Icon className="w-4 h-4" />}
          {item.label}
        </span>
      )
    }

    return (
      <Link
        key={item.href}
        to={item.href}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          active
            ? 'bg-copper-50 text-copper-700'
            : 'text-warm-600 hover:bg-warm-50 hover:text-midnight-800'
        }`}
      >
        {Icon && <Icon className="w-4 h-4" />}
        {item.label}
      </Link>
    )
  }

  const renderSection = (section: NavSection) => {
    if (!canViewSection(section)) return null

    const isDeferred = section.status === 'deferred'
    const sectionActive = isSectionActive(section)
    const isCollapsed = collapsed[section.id] ?? false
    const needsTenantContext = section.requireActiveTenant && !hasTenantContext && isStaff

    // Single-link section (Home)
    if (section.href && !section.items) {
      return (
        <div key={section.id}>
          <Link
            to={section.href}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive(section.href, true)
                ? 'bg-copper-50 text-copper-700'
                : 'text-warm-700 hover:bg-warm-50 hover:text-midnight-800'
            }`}
          >
            <section.icon className="w-[18px] h-[18px]" />
            {section.label}
          </Link>
        </div>
      )
    }

    // Collapsible section
    return (
      <div key={section.id}>
        <button
          onClick={() => {
            if (isDeferred) return
            if (needsTenantContext) {
              setTenantPickerTarget({ name: section.label, href: section.items?.[0]?.href || '/' })
              return
            }
            toggleSection(section.id)
          }}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isDeferred
              ? 'text-warm-300 cursor-not-allowed'
              : needsTenantContext
                ? 'text-warm-400 hover:bg-warm-50 hover:text-warm-600'
                : sectionActive
                  ? 'text-copper-700'
                  : 'text-warm-700 hover:bg-warm-50 hover:text-midnight-800'
          }`}
        >
          <span className="flex items-center gap-2.5">
            <section.icon className="w-[18px] h-[18px]" />
            {section.label}
            {isDeferred && (
              <span className="text-[10px] bg-warm-100 text-warm-400 px-1.5 py-0.5 rounded font-medium">
                TBD
              </span>
            )}
          </span>
          {!isDeferred && !needsTenantContext && section.items && (
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${
                !isCollapsed ? 'rotate-180' : ''
              }`}
            />
          )}
        </button>

        <AnimatePresence initial={false}>
          {!isCollapsed && !isDeferred && !needsTenantContext && section.items && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="ml-3 pl-3 border-l border-warm-200 mt-1 space-y-0.5">
                {section.items.map(item => renderNavItem(item, false))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <>
      <aside className="w-64 bg-white border-r border-warm-200 flex flex-col flex-shrink-0">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-warm-200">
          <Link to="/" className="flex items-center">
            <span className="text-xl font-heading font-bold text-midnight-800">Personic</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navigation.map((section) => {
            const divider = section.dividerBefore
            return (
              <div key={section.id}>
                {divider && canViewSection(section) && (
                  <div className="pt-4 pb-2 px-3">
                    <span className="text-[10px] font-semibold text-warm-400 uppercase tracking-widest">
                      {divider}
                    </span>
                  </div>
                )}
                {renderSection(section)}
              </div>
            )
          })}

          {/* Tools divider + Extension link */}
          <div className="pt-4 pb-2 px-3">
            <span className="text-[10px] font-semibold text-warm-400 uppercase tracking-widest">
              TOOLS
            </span>
          </div>
          <Link
            to="/extension"
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive('/extension')
                ? 'bg-copper-50 text-copper-700'
                : 'text-warm-700 hover:bg-warm-50 hover:text-midnight-800'
            }`}
          >
            <Download className="w-[18px] h-[18px]" />
            Extension
          </Link>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-warm-200">
          {isImpersonating && (
            <div className="mb-2 px-2 py-1 rounded bg-amber-50 border border-amber-200">
              <p className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold">Impersonating</p>
              <p className="text-xs text-amber-700 truncate">{tenantName}</p>
            </div>
          )}
          <div className="flex items-center">
            <div className="w-9 h-9 rounded-full bg-copper-100 flex items-center justify-center">
              <span className="text-copper-600 font-semibold text-sm">
                {user?.name?.charAt(0).toUpperCase() || '?'}
              </span>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-midnight-800 truncate">
                {user?.name}
              </p>
              <p className="text-xs text-warm-500 truncate">
                {roleLabel}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-warm-600 hover:bg-warm-50 hover:text-midnight-800 transition-colors border border-warm-200"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </aside>

      {/* Tenant Picker Modal */}
      {tenantPickerTarget && (
        <TenantPickerModal
          targetModule={tenantPickerTarget.name}
          targetPath={tenantPickerTarget.href}
          onClose={() => setTenantPickerTarget(null)}
        />
      )}
    </>
  )
}
